import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBlogBySlug } from "../../Apis/blogs";
import { IMAGES } from "../../assets/img/ImageIndex";
import Slider from "./Slider";
import BlogModal from "./CreateBlogModal";
import "../../assets/css/admin.css";

function AdminBlogDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchBlog(slug);
    }
  }, [slug]);

  const fetchBlog = async (blogSlug) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBlogBySlug(blogSlug);
      
      if (response.data && response.data.success && response.data.data) {
        setBlog(response.data.data);
      } else {
        setError('Blog not found');
      }
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError('Failed to fetch blog');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const createMarkup = (htmlContent) => {
    return { __html: htmlContent };
  };

  const handleEdit = () => {
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <Slider />
        <div className="admin-main-content">
          <div className="admin-breadcrumb">
            <div className="admin-breadcrumb-text">Admin / Blog Details</div>
          </div>
          <div className="admin-content-area">
            <div className="text-center py-8">
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '4px solid #f3f3f3', 
                borderTop: '4px solid #3b82f6', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
              <p className="mt-3">Loading blog...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="admin-dashboard">
        <Slider />
        <div className="admin-main-content">
          <div className="admin-breadcrumb">
            <div className="admin-breadcrumb-text">Admin / Blog Details</div>
          </div>
          <div className="admin-content-area">
            <div className="text-center py-8">
              <div className="admin-error-message">
                {error || 'Blog not found'}
              </div>
              <button 
                onClick={() => navigate('/admin/blogs')} 
                className="admin-create-btn mt-3"
              >
                Back to Blogs
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <Slider />
      <div className="admin-main-content">
        <div className="admin-breadcrumb">
          <div className="admin-breadcrumb-text">Admin / Blog Details / {blog.title}</div>
        </div>

        <div className="admin-content-area">
          {/* Blog Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h1 className="admin-welcome-title">{blog.title}</h1>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                onClick={handleEdit}
                className="admin-action-btn admin-edit-btn"
              >
                Edit Blog
              </button>
              <button 
                onClick={() => navigate('/admin/blogs')}
                className="admin-action-btn admin-view-btn"
              >
                Back to Blogs
              </button>
            </div>
          </div>

          {/* Blog Meta Information */}
          <div className="admin-section mb-4">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <div className="admin-blog-meta">
                  <p><strong>Blog ID:</strong> {blog.blog_id}</p>
                  <p><strong>Slug:</strong> {blog.slug}</p>
                  <p><strong>Created:</strong> {formatDate(blog.created_at)}</p>
                  <p><strong>Last Updated:</strong> {blog.updated_at ? formatDate(blog.updated_at) : 'Never'}</p>
                </div>
              </div>
              <div>
                <div className="admin-blog-meta">
                  <p><strong>Status:</strong> <span style={{ background: '#10b981', color: 'white', padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem' }}>Published</span></p>
                  <p><strong>Author:</strong> Admin</p>
                  <p><strong>Comments:</strong> 0</p>
                  <p><strong>Table of Content:</strong> {blog.table_of_content || 'None'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Blog Image */}
          {blog.image_path && (
            <div className="admin-section mb-4">
              <h3 className="admin-section-title">Featured Image</h3>
              <div className="text-center">
                <img 
                  src={blog.image_path} 
                  alt={blog.title} 
                  className="img-fluid rounded"
                  style={{ maxHeight: '400px', objectFit: 'cover' }}
                  onError={(e) => e.target.src = IMAGES.imageNotFound}
                />
              </div>
            </div>
          )}

          {/* Blog Content */}
          <div className="admin-section mb-4">
            <h3 className="admin-section-title">Blog Content</h3>
            <div className="blog-content-admin">
              <div dangerouslySetInnerHTML={createMarkup(blog.content)} />
            </div>
          </div>

          {/* Short Description */}
          {blog.short_description && (
            <div className="admin-section mb-4">
              <h3 className="admin-section-title">Short Description</h3>
              <p>{blog.short_description}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="admin-section">
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button 
                onClick={handleEdit}
                className="admin-modal-btn admin-modal-btn-submit"
              >
                Edit This Blog
              </button>
              <button 
                onClick={() => navigate('/admin/blogs')}
                className="admin-modal-btn admin-modal-btn-cancel"
              >
                Back to Blog List
              </button>
              <a 
                href={`/blog-details/${blog.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="admin-action-btn admin-view-btn text-decoration-none"
              >
                View on Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <BlogModal
          blogId={blog.blog_id}
          onClose={() => {
            setShowModal(false);
            fetchBlog(slug);
          }}
        />
      )}
    </div>
  );
}

export default AdminBlogDetails;
